import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type Status = "idle" | "submitting" | "success" | "error";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dbb5ruq9ke8mc8ad7ek0";

export default function AboutPage() {
  const location = useLocation();
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useDocumentMeta({
    title: "关于我们与联系方式 - 亚洲激情视频",
    description:
      "了解亚洲激情视频的站点定位、内容组织方式、使用说明与免责声明，并可通过页面反馈表单向我们提交意见与建议。",
    keywords: "亚洲激情视频,关于我们,联系方式,视频聚合平台,免责声明",
  });

  useEffect(() => {
    if (location.hash === "#contact") {
      const target = document.getElementById("contact");
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [location.hash]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setSuccessMessage("感谢反馈，我们已收到你的信息。");
      setFormError("");
      form.reset();
      return;
    }

    data.delete("website_alt");
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const serverMessage =
        (typeof meta.message === "string" && meta.message) ||
        (typeof parsed?.message === "string" && parsed.message) ||
        (typeof meta.detail === "string" && meta.detail) ||
        responseText;

      const message = String(serverMessage ?? "");
      const isSpam = message.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          isSpam || !message
            ? "提交内容未通过校验，请调整后重试。"
            : message,
        );
        return;
      }

      setStatus("success");
      setSuccessMessage(
        (typeof meta.message === "string" && meta.message) || "提交成功，感谢你的反馈！",
      );
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，表单未能提交，请检查网络后重试。");
    }
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-800 bg-gradient-to-b from-primary-950/60 to-background-950">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于与联系" }]} />
          <h1 className="mt-5 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-50 md:text-4xl">
            关于亚洲激情视频
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-400 md:text-base">
            我们是一个以分类、筛选与资料展示为核心的亚洲影像内容聚合站点。比起堆砌入口，我们更在意每一部作品是否被放在了正确的位置上。
          </p>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-5">
            {[
              { value: "6", label: "内容分类" },
              { value: "30", label: "示例片目" },
              { value: "4", label: "视频类型" },
              { value: "18+", label: "成年用户" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading text-2xl font-bold text-foreground-50">{stat.value}</p>
                <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-14">
          <div className="lg:col-span-2">
            <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-primary-400">
              <span className="h-px w-6 bg-primary-600" />
              Our position
            </p>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              专业视频聚合，而不是随机跳转
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-400">
              亚洲激情视频面向对亚洲影像内容感兴趣的成年观众。站内的每一部作品都被归入六个主题频道之一，并标注视频类型、时长、发布日期与标签。你可以先按频道进入，再用筛选条件缩小范围，最后在详情页看到完整的基本信息与同类推荐。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              我们也把「找得到」放在「看得多」前面：搜索会同时匹配标题、简介、标签与分类，避免你在一堆相似条目里反复翻页。相关推荐则依据标签重合度与分类归属排序，确保推荐结果与当前内容真的相关。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              需要说明的是，本站在当前阶段是一个完整的界面与交互演示：所有视频标题、封面、简介与数据均为虚构示例，不具备真实片源，也不代表任何真实人物或作品。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: "ri-database-2-line", title: "资料结构化", desc: "统一字段，便于比较与筛选。" },
                { icon: "ri-route-line", title: "路径清晰", desc: "首页到详情，层级不超过三级。" },
                { icon: "ri-shield-check-line", title: "内容规范", desc: "标注演示属性与免责声明。" },
              ].map((item) => (
                <div key={item.title} className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-950 text-primary-300">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-100">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
              <span className="h-3.5 w-1 rounded-full bg-accent-500" />
              使用说明
            </h2>
            <ol className="mt-4 space-y-4">
              {[
                "从首页的主题入口或顶部「分类」菜单进入频道，先确定内容方向。",
                "在分类页使用类型、时长与发布日期筛选，结果会实时刷新。",
                "直接点击卡片进入详情页，查看简介、标签与同分类推荐。",
                "需要精准查找时使用搜索页，关键词会匹配标题、标签与分类。",
              ].map((text, index) => (
                <li key={text} className="flex gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background-800 text-[11px] font-medium text-primary-300">
                    {index + 1}
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-400">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="mt-12 rounded-lg border border-accent-900/60 bg-accent-950/25 p-5 md:p-6">
          <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-accent-200">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-alert-line text-base" />
            </span>
            免责声明
          </h2>
          <div className="mt-3 space-y-2 text-xs leading-relaxed text-foreground-400">
            <p>
              本站为内容聚合类产品的界面演示，站内所有视频标题、封面图像、简介、访问量与发布时间均为虚构数据，不对应任何真实人物、机构或作品。
            </p>
            <p>
              站内不提供、不存储、不分发任何真实视频文件，播放器仅模拟播放状态与进度，不承诺任何可播放内容。分享功能仅复制站内链接，不会对外部社交平台发起请求。
            </p>
            <p>
              本站面向 18 岁以上成年用户。若你未满法定年龄，请立即停止浏览并关闭页面。
            </p>
          </div>
        </div>
      </section>

      <section id="contact" className="border-t border-background-800 bg-background-900/50">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
            <div className="lg:col-span-2">
              <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-primary-400">
                <span className="h-px w-6 bg-primary-600" />
                Contact
              </p>
              <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
                联系我们与意见反馈
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-400">
                如果你对分类结构、筛选逻辑或页面体验有想法，欢迎通过右侧表单告诉我们。反馈会直接进入站点表单系统，我们会定期阅读。
              </p>

              <div className="mt-6 space-y-3">
                {[
                  { icon: "ri-mail-line", label: "反馈邮箱", value: "feedback@example.com" },
                  { icon: "ri-time-line", label: "响应时间", value: "工作日内 1 - 2 天" },
                  { icon: "ri-map-pin-line", label: "站点性质", value: "线上内容聚合演示站点" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 rounded-md border border-background-800 bg-background-900 px-4 py-3"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded bg-background-800 text-primary-300">
                      <i className={`${item.icon} text-sm`} />
                    </span>
                    <div>
                      <p className="text-[11px] text-foreground-600">{item.label}</p>
                      <p className="mt-0.5 text-sm text-foreground-200">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-[11px] leading-relaxed text-foreground-600">
                以上联系方式为演示信息，邮箱不作真实收件用途。
              </p>
            </div>

            <div className="lg:col-span-3">
              <div className="rounded-lg border border-background-800 bg-background-900 p-5 md:p-7">
                <h3 className="font-heading text-base font-semibold text-foreground-100">
                  提交反馈
                </h3>
                <p className="mt-1.5 text-xs text-foreground-500">
                  带 <span className="text-primary-300">*</span> 的字段为必填项，反馈内容请控制在 500 字以内。
                </p>

                <form
                  id="site-feedback-form"
                  data-readdy-form
                  action={FORM_SUBMIT_ADDR}
                  method="post"
                  onSubmit={handleSubmit}
                  className="mt-6"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="extra-field-verify"
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label htmlFor="feedback-name" className="mb-1.5 block text-xs text-foreground-400">
                        昵称 <span className="text-primary-400">*</span>
                      </label>
                      <input
                        id="feedback-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="box-border h-10 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                    <div className="min-w-0">
                      <label htmlFor="feedback-email" className="mb-1.5 block text-xs text-foreground-400">
                        邮箱 <span className="text-primary-400">*</span>
                      </label>
                      <input
                        id="feedback-email"
                        name="email"
                        type="email"
                        required
                        maxLength={80}
                        placeholder="you@example.com"
                        className="box-border h-10 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="mt-4 min-w-0">
                    <label htmlFor="feedback-topic" className="mb-1.5 block text-xs text-foreground-400">
                      反馈类型
                    </label>
                    <select
                      id="feedback-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="box-border h-10 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="分类与筛选">分类与筛选</option>
                      <option value="页面体验">页面体验</option>
                      <option value="合作咨询">合作咨询</option>
                    </select>
                  </div>

                  <div className="mt-4 min-w-0">
                    <label htmlFor="feedback-message" className="mb-1.5 block text-xs text-foreground-400">
                      反馈内容 <span className="text-primary-400">*</span>
                    </label>
                    <textarea
                      id="feedback-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="说说你的想法，最多 500 字"
                      className="box-border w-full resize-none rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm leading-relaxed text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  {status === "error" && formError && (
                    <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-800 bg-primary-950/60 px-3 py-2.5">
                      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                        <i className="ri-error-warning-line text-sm text-primary-300" />
                      </span>
                      <p className="text-xs leading-relaxed text-primary-200">{formError}</p>
                    </div>
                  )}

                  {status === "success" && (
                    <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-800 bg-accent-950/40 px-3 py-2.5">
                      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                        <i className="ri-checkbox-circle-line text-sm text-accent-300" />
                      </span>
                      <p className="text-xs leading-relaxed text-accent-100">
                        {successMessage || "提交成功，感谢你的反馈！"}
                      </p>
                    </div>
                  )}

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-foreground-600">
                      提交即表示你已阅读并同意本站的演示与免责说明。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-sm" />
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-sm" />
                          提交反馈
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-foreground-600">
                如需继续浏览，<Link to="/" className="text-primary-300 hover:text-primary-200">返回首页</Link>
                ，或前往<Link to="/search" className="text-primary-300 hover:text-primary-200">搜索页</Link>查找内容。
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}